import React from 'react';
import { configurationUtils } from '@codametrix/ui-common';
import { ColDef, ICellRendererParams } from 'ag-grid-community';
import { Button } from '@chakra-ui/react';
import { ButtonSize, ButtonVariant } from '../../core/enums';
import EditableConfigRow from './EditableConfigRow';

const {serviceLineMatches} = configurationUtils;


export interface ConfigurationRowData {
    serviceLine: string;
    value: any;
    editableValue: string;
    updatedBy: string;
    updatedDate: string;
    defaultValue: any;
    rawConfig: CMxAPI.ConfigurationValue;
    isJsonValue: boolean;
    isTenantDefault: boolean;
    hasExactMatch: boolean;
    inheritanceMessage?: string | null;
    effectiveType?: string;
    fallbackSource?: string;
}

export interface TenantDefaultConfig {
    tenantDefault: CMxAPI.ConfigurationValue | undefined;
    globalDefault: CMxAPI.ConfigurationValue | undefined;
    displayValue: any;
    inheritanceMessage: string | null;
    editableValue: string;
    rawConfig: CMxAPI.ConfigurationValue;
}
const { getConfigDisplayValue } = configurationUtils;


// Formats the configuration display value. If multiple values exist, they are wrapped in square brackets.
// Optionally includes a label ("Value:" or "MultiValue:") based on the includeLabel flag.
export function formatConfigDisplayValue(
  value: string | string[] | object | null | undefined,
  includeLabel: boolean = false
): JSX.Element | string {
  if (!value || (Array.isArray(value) && value.length === 0)) {
    return <span style={{ color: 'gray' }}>null</span>;
  }

  // Handle objects (jsonValue)
  if (typeof value === 'object' && !Array.isArray(value)) {
    const jsonString = JSON.stringify(value, null, 2);
    return (
      <pre
        style={{
          whiteSpace: 'pre-wrap',
          fontSize: '12px',
          backgroundColor: '#f5f5f5',
          padding: '8px',
          borderRadius: '4px',
          maxHeight: '200px',
          overflow: 'auto'
        }}>
        {includeLabel ? `JsonValue: ${jsonString}` : jsonString}
      </pre>
    );
  }

  if (Array.isArray(value)) {
    if (value.length > 1) {
      const formatted = `[${value.join(', ')}]`;
      return includeLabel ? `MultiValue: ${formatted}` : formatted;
    }
    return includeLabel ? `Value: ${value[0]}` : value[0];
  }

  return includeLabel ? `Value: ${value}` : value;
}

// Formats a timestamp into a localized date string, or returns "Unknown" if the timestamp is not provided.
export function formatTimestamp(
  timestamp: number | string | null | undefined
): string {
  if (!timestamp) return 'Unknown';
  return new Date(+timestamp).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
}

/**
 * Find and prepare tenant default configuration data
 */
export const prepareTenantDefaultConfig = (
    configDetail: CMxAPI.Configuration,
    tenantId: string
): TenantDefaultConfig => {
    const tenantDefault = configDetail.values.find(
        v => v.tenantId === tenantId && v.serviceLines?.includes('*')
    );

    const globalDefault = configDetail.values.find(
        v => v.tenantId === '*' && v.serviceLines?.includes('*')
    );

    let displayValue: any;
    let inheritanceMessage: string | null = null;

    if (tenantDefault) {
        displayValue = getConfigDisplayValue(tenantDefault);
    } else {
        // Check what the tenant default would inherit from
        const otherTenantDefault = configDetail.values.find(
            v => v.tenantId !== tenantId &&
                v.tenantId !== '*' &&
                v.serviceLines?.includes('*')
        );

        if (otherTenantDefault) {
            displayValue = getConfigDisplayValue(otherTenantDefault);
            inheritanceMessage = 'parent tenant';
        } else if (globalDefault) {
            displayValue = getConfigDisplayValue(globalDefault);
            inheritanceMessage = 'system default';
        } else {
            displayValue = 'Not Set';
        }
    }

    // Create editable value for tenant default
    let editableValue: string;
    if (tenantDefault) {
        if (tenantDefault.jsonValue !== null && tenantDefault.jsonValue !== undefined) {
            editableValue = JSON.stringify(tenantDefault.jsonValue, null, 2);
        } else {
            editableValue = tenantDefault.value || '';
        }
    } else {
        editableValue = '';
    }

    // Create rawConfig for tenant default
    const templateConfig = configDetail.values[0] || {};
    const rawConfig = tenantDefault || {
        id: 0,
        configurationId: configDetail.id,
        configurationKeyname: configDetail.keyname,
        tenantId,
        serviceLines: ['*'],
        value: '',
        jsonValue: templateConfig.jsonValue !== undefined ? null : undefined,
        version: templateConfig.version || null,
        sourceType: templateConfig.sourceType || null,
        createdDate: null,
        updatedDate: null,
        jiraTicket: null,
        jiraPurpose: null
    };

    return {
        tenantDefault,
        globalDefault,
        displayValue,
        inheritanceMessage,
        editableValue,
        rawConfig
    };
};

/**
 * Build the tenant default row
 */
export const buildTenantDefaultRow = (
    configDetail: CMxAPI.Configuration,
    tenantId: string,
    isSystemLevel: boolean = false
): ConfigurationRowData => {
    const {
        tenantDefault,
        globalDefault,
        displayValue,
        inheritanceMessage,
        editableValue,
        rawConfig
    } = prepareTenantDefaultConfig(configDetail, tenantId);

    return {
        serviceLine: '*',
        value: displayValue,
        editableValue,
        updatedBy: tenantDefault ? 'System' : 'Not Set',
        updatedDate: formatTimestamp(tenantDefault?.updatedDate),
        defaultValue: globalDefault ? getConfigDisplayValue(globalDefault) : null,
        rawConfig,
        isJsonValue: rawConfig.jsonValue !== null && rawConfig.jsonValue !== undefined,
        isTenantDefault: true,
        hasExactMatch: !!tenantDefault,
        inheritanceMessage: isSystemLevel ? 'System Default' : inheritanceMessage
    };
};

/**
 * Prepare service line configuration data
 */
export const prepareServiceLineConfig = (
    serviceLine: CMxAPI.ServiceLine,
    configDetail: CMxAPI.Configuration,
    tenantId: string,
    getEffectiveValueForServiceLine: (name: string) => any
) => {
    const effectiveConfig = getEffectiveValueForServiceLine(serviceLine.name);


    const exactMatch = configDetail.values.find(
        v => v.tenantId === tenantId &&
            serviceLineMatches(v, serviceLine.name, false)
    );

    // For editing purposes, convert the value to a string
    let editableValue: string;
    if (exactMatch) {
        if (exactMatch.jsonValue !== null && exactMatch.jsonValue !== undefined) {
            editableValue = JSON.stringify(exactMatch.jsonValue, null, 2);
        } else {
            editableValue = exactMatch.value || '';
        }
    } else {
        editableValue = '';
    }

    return {
        effectiveConfig,
        exactMatch,
        editableValue
    };
};

/**
 * Determine fallback value and source for a service line
 */
export const determineFallbackValue = (
    exactMatch: CMxAPI.ConfigurationValue | undefined,
    effectiveConfig: any,
    tenantDefault: CMxAPI.ConfigurationValue | undefined,
    globalDefault: CMxAPI.ConfigurationValue | undefined
) => {
    let fallbackValue: any;
    let fallbackSource: string;

    if (exactMatch) {
        // If there's an exact match, the fallback would be tenant default or global default
        if (tenantDefault) {
            fallbackValue = getConfigDisplayValue(tenantDefault);
            fallbackSource = 'tenant default';
        } else if (globalDefault) {
            fallbackValue = getConfigDisplayValue(globalDefault);
            fallbackSource = 'system default';
        } else {
            fallbackValue = null;
            fallbackSource = 'none';
        }
    } else {
        // If no exact match, show what it's currently inheriting from
        fallbackValue = effectiveConfig.value;
        fallbackSource = effectiveConfig.inheritanceMessage || 'none';
    }

    return { fallbackValue, fallbackSource };
};

/**
 * Create raw config for service line
 */
export const createServiceLineRawConfig = (
    exactMatch: CMxAPI.ConfigurationValue | undefined,
    serviceLine: CMxAPI.ServiceLine,
    configDetail: CMxAPI.Configuration,
    tenantId: string
): CMxAPI.ConfigurationValue => {
    if (exactMatch) return exactMatch;

    const templateConfig = configDetail.values[0] || {};

    return {
        id: 0,
        configurationId: configDetail.id,
        configurationKeyname: configDetail.keyname,
        tenantId,
        serviceLines: [serviceLine.name],
        value: '',
        jsonValue: templateConfig.jsonValue !== undefined ? null : undefined,
        version: templateConfig.version || null,
        sourceType: templateConfig.sourceType || null,
        createdDate: null,
        updatedDate: null,
        jiraTicket: null,
        jiraPurpose: null
    };
};

/**
 * Build a single service line row
 */
export const buildServiceLineRow = (
    serviceLine: CMxAPI.ServiceLine,
    configDetail: CMxAPI.Configuration,
    tenantId: string,
    getEffectiveValueForServiceLine: (name: string) => any,
    tenantDefault: CMxAPI.ConfigurationValue | undefined,
    globalDefault: CMxAPI.ConfigurationValue | undefined
): ConfigurationRowData => {
    const {
        effectiveConfig,
        exactMatch,
        editableValue
    } = prepareServiceLineConfig(serviceLine, configDetail, tenantId, getEffectiveValueForServiceLine);

    const { fallbackValue, fallbackSource } = determineFallbackValue(
        exactMatch,
        effectiveConfig,
        tenantDefault,
        globalDefault
    );

    const rawConfig = createServiceLineRawConfig(exactMatch, serviceLine, configDetail, tenantId);

    return {
        serviceLine: serviceLine.name,
        value: effectiveConfig.value, // This shows the actual effective value
        editableValue,
        updatedBy: exactMatch ? 'System' : 'Not Set',
        updatedDate: formatTimestamp(exactMatch?.updatedDate),
        defaultValue: fallbackValue, // This shows what the fallback would be
        fallbackSource, // Track where the fallback comes from
        rawConfig,
        isJsonValue: rawConfig.jsonValue !== null && rawConfig.jsonValue !== undefined,
        isTenantDefault: false,
        hasExactMatch: !!exactMatch, // Track if there's an exact match for this service line
        effectiveType: effectiveConfig.type, // Track what type of config is being used
        inheritanceMessage: effectiveConfig.inheritanceMessage
    };
};

/**
 * Build service line rows
 */
export const buildServiceLineRows = (
    serviceLines: CMxAPI.ServiceLine[],
    configDetail: CMxAPI.Configuration,
    tenantId: string,
    getEffectiveValueForServiceLine: (name: string) => any
): ConfigurationRowData[] => {
    const { tenantDefault, globalDefault } = prepareTenantDefaultConfig(configDetail, tenantId);

    return serviceLines.map(serviceLine =>
        buildServiceLineRow(
            serviceLine,
            configDetail,
            tenantId,
            getEffectiveValueForServiceLine,
            tenantDefault,
            globalDefault
        )
    );
};

/**
 * Main function to build all configuration row data
 */
export const buildConfigurationRowData = (
    configDetail: CMxAPI.Configuration | null,
    serviceLines: CMxAPI.ServiceLine[],
    tenantId: string,
    getEffectiveValueForServiceLine: (name: string) => any,
    isSystemLevel: boolean = false
): ConfigurationRowData[] => {
    if (!configDetail) return [];

    const rows: ConfigurationRowData[] = [];

    // Add tenant default row first (or system default if system level)
    rows.push(buildTenantDefaultRow(configDetail, tenantId, isSystemLevel));

    // Always add service line-specific rows if service lines are available
    if (serviceLines.length > 0) {
        rows.push(...buildServiceLineRows(serviceLines, configDetail, tenantId, getEffectiveValueForServiceLine));
    }

    return rows;
};


/**
 * Renders the service line cell with appropriate styling and inheritance messages
 */
export const renderServiceLineCell = (params: ICellRendererParams, isSystemLevel: boolean = false): JSX.Element => {
    const { serviceLine, isTenantDefault, hasExactMatch, inheritanceMessage } = params.data;

    return (
        <div>
            <strong>
                {serviceLine === '*'
                    ? (isSystemLevel ? 'System Default' : 'Tenant Default')
                    : serviceLine}
            </strong>
            {isTenantDefault && (
                <div style={{ fontSize: '0.8em', color: '#666', fontStyle: 'italic' }}>
                    {isSystemLevel
                        ? 'Default for all tenants and service lines'
                        : 'Default for all service lines'}
                </div>
            )}
            {!isTenantDefault && hasExactMatch && (
                <div style={{ fontSize: '0.8em', color: '#0066cc', fontStyle: 'italic' }}>
                    Service line override
                </div>
            )}
        </div>
    );
};

/**
 * Renders the value cell with editing capabilities and inheritance information
 */
export const renderValueCell = (
    params: ICellRendererParams,
    editingServiceLine: string | null,
    setEditingServiceLine: (serviceLine: string | null) => void,
    handleSaveRow: (data: any) => void,
    isSystemLevel: boolean = false
): JSX.Element => {
    const {
        serviceLine,
        value,
        editableValue,
        updatedBy,
        updatedDate,
        rawConfig,
        isJsonValue,
        isTenantDefault,
        hasExactMatch,
        fallbackSource,
        inheritanceMessage
    } = params.data;

    const isEditing = editingServiceLine === serviceLine;

    if (isEditing) {
        return (
            <div style={{ padding: '12px 0' }}>
                <EditableConfigRow
                    data={{
                        ...params.data,
                        value: editableValue
                    }}
                    onCancel={() => setEditingServiceLine(null)}
                    onSave={({ newValue, jiraTicket, jiraPurpose }) =>
                        handleSaveRow({
                            serviceLine,
                            newValue,
                            jiraTicket,
                            jiraPurpose,
                            rawConfig
                        })
                    }
                />
            </div>
        );
    }

    return (
        <div style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
            <div style={{
                color: hasExactMatch ? 'inherit' : '#666',
                fontStyle: hasExactMatch ? 'normal' : 'italic'
            }}>
                {formatConfigDisplayValue(value, true)}
            </div>
            {isJsonValue && (
                <div
                    style={{
                        color: '#0066cc',
                        fontSize: '0.7em',
                        marginTop: '2px',
                        fontStyle: 'italic'
                    }}>
                    JSON Configuration
                </div>
            )}
            {isSystemLevel && (
                <div
                    style={{
                        color: '#805ad5',
                        fontSize: '0.7em',
                        marginTop: '2px',
                        fontStyle: 'italic'
                    }}>
                    System-Level Configuration
                </div>
            )}
            <div
                style={{
                    color: 'gray',
                    fontSize: '0.75em',
                    marginTop: '4px'
                }}>
                {hasExactMatch ? (
                    <>Last updated by {updatedBy} on {updatedDate}</>
                ) : (
                    <>Inheriting from {inheritanceMessage || fallbackSource}</>
                )}
            </div>
            <div style={{ textAlign: 'left', marginTop: '.5rem' }}>
                <Button
                    size={ButtonSize.SM}
                    variant={ButtonVariant.PRIMARY}
                    onClick={() => setEditingServiceLine(serviceLine)}>
                    {hasExactMatch ? 'Edit' : 'Override'}
                </Button>
            </div>
        </div>
    );
};

/**
 * Builds the service line column definition
 */
export const buildServiceLineColumnDef = (isSystemLevel: boolean = false): ColDef => ({
    headerName: 'Service Line',
    field: 'serviceLine',
    flex: 1,
    autoHeight: true,
    wrapText: true,
    cellRenderer: (params: ICellRendererParams) => renderServiceLineCell(params, isSystemLevel)
});

/**
 * Builds the value column definition with editing capabilities
 */
export const buildValueColumnDef = (
    editingServiceLine: string | null,
    setEditingServiceLine: (serviceLine: string | null) => void,
    handleSaveRow: (data: any) => void,
    isSystemLevel: boolean = false
): ColDef => ({
    headerName: 'Value',
    field: 'value',
    flex: 2,
    autoHeight: true,
    wrapText: true,
    cellRenderer: (params: ICellRendererParams) =>
        renderValueCell(params, editingServiceLine, setEditingServiceLine, handleSaveRow, isSystemLevel)
});

/**
 * Main function to build all column definitions
 */
export const buildConfigurationColumnDefs = (
    editingServiceLine: string | null,
    setEditingServiceLine: (serviceLine: string | null) => void,
    handleSaveRow: (data: any) => void,
    isSystemLevel: boolean = false
): ColDef[] => [
    buildServiceLineColumnDef(isSystemLevel),
    buildValueColumnDef(editingServiceLine, setEditingServiceLine, handleSaveRow, isSystemLevel)
];

